import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Music2, Instagram, Facebook, Youtube, Flag, Clock } from "lucide-react";
import { riskStyles, confidenceStyles } from "../lib/risk";

const platformIcons = { TikTok: Music2, Instagram, Facebook, YouTube: Youtube };

export const PlatformBadge = ({ name }) => {
  const Icon = platformIcons[name];
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#E5DFD7] bg-white px-2 py-0.5 font-mono text-[10px] font-medium text-[#525E54]">
      {Icon && <Icon className="w-3 h-3" strokeWidth={2.2} />}
      {name}
    </span>
  );
};

const TypeChip = ({ type }) => {
  if (type === "case") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FDA4AF] bg-[#FFE4E6] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#9F1239]">
        Real-World Case Study
        <span className="hidden sm:inline font-medium normal-case tracking-normal text-[#9F1239]/70">
          • Lizard-Checked Sep 18–21, 2026
        </span>
      </span>
    );
  }
  if (type === "demo") {
    return (
      <span className="inline-flex items-center rounded-full border border-[#E5DFD7] bg-[#F4EFEA] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#7B887D]">
        Demo Report • Fictional
      </span>
    );
  }
  return null;
};

export const ScamCard = ({ scam, index }) => {
  const risk = riskStyles[scam.label];
  const [quipFrame, setQuipFrame] = useState(1);
  const quipTimers = useRef([]);

  useEffect(() => {
    if (!scam.quip) return;
    ["/lzrd-1.png", "/lzrd-2.png"].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    return () => quipTimers.current.forEach(clearTimeout);
  }, [scam.quip]);

  const startQuipShake = () => {
    if (!scam.quip || !window.matchMedia("(hover: hover)").matches) return;
    quipTimers.current.forEach(clearTimeout);
    quipTimers.current = [2, 1, 2, 1, 2].map((frame, i) =>
      setTimeout(() => setQuipFrame(frame), i * 175)
    );
  };

  const resetQuip = () => {
    if (!scam.quip) return;
    quipTimers.current.forEach(clearTimeout);
    quipTimers.current = [];
    setQuipFrame(1);
  };

  return (
    <motion.article
      onMouseEnter={startQuipShake}
      onMouseLeave={resetQuip}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
      className="relative rounded-3xl border border-[#E5DFD7] bg-white p-4 sm:p-5 shadow-[0_1px_2px_rgba(20,25,21,0.05)] hover:shadow-[0_12px_32px_-12px_rgba(20,25,21,0.18)] transition-shadow duration-300"
      data-testid={`scheme-card-${scam.rank}`}
    >
      {scam.quip && (
        <div
          className="absolute -top-6 right-3 sm:right-4 flex items-center gap-2.5 whitespace-nowrap rounded-full bg-[#141915] text-white pl-1.5 pr-4 py-1.5 shadow-lg rotate-2 border border-white/10"
          data-testid="quip-badge"
        >
          <span className="relative block w-11 h-11 sm:w-12 sm:h-12 shrink-0 overflow-hidden rounded-full ring-2 ring-[#22C55E]/40 bg-black">
            <img
              src={`/lzrd-${quipFrame}.png`}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              data-testid="quip-lizard-image"
            />
          </span>
          <span className="font-mono text-xs sm:text-sm font-bold tracking-wide">{scam.quip}</span>
        </div>
      )}
      <div className="flex gap-3.5 sm:gap-5">
        <div className="shrink-0 w-9 sm:w-12 pt-0.5 text-center">
          <span className="font-mono font-bold text-2xl sm:text-3xl tracking-tighter text-[#141915]/15">
            {String(scam.rank).padStart(2, "0")}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <h3 className="font-display text-lg sm:text-xl font-semibold text-[#141915] leading-tight">
              {scam.name}
            </h3>
            <span
              className="rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: risk.bg, color: risk.text, borderColor: risk.border }}
            >
              {scam.label}
            </span>
            <TypeChip type={scam.type} />
          </div>
          <p className="mt-1 text-sm text-[#525E54] leading-snug">{scam.desc}</p>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {scam.platforms.map((p) => (
              <PlatformBadge key={p} name={p} />
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[#7B887D]">Risk</span>
              <span className="font-mono font-bold text-base tracking-tighter" style={{ color: risk.text }}>
                {scam.score.toFixed(1)}
              </span>
              <span className="relative h-1.5 w-16 sm:w-20 overflow-hidden rounded-full bg-[#F4EFEA]">
                <motion.span
                  initial={{ width: 0 }}
                  whileInView={{ width: `${scam.score * 10}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ backgroundColor: risk.bar }}
                />
              </span>
            </div>
            <span
              className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${confidenceStyles[scam.confidence]}`}
            >
              Evidence: {scam.confidence}
            </span>
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {scam.flags.map((f) => (
              <span
                key={f}
                className="inline-flex items-center gap-1 rounded-md bg-[#F4EFEA] px-1.5 py-0.5 text-[10px] font-medium text-[#525E54]"
              >
                <Flag className="w-2.5 h-2.5 text-[#C2410C]" strokeWidth={2.5} />
                {f}
              </span>
            ))}
          </div>

          {scam.hasReport ? (
            <Link
              to={`/report/${scam.slug}`}
              data-testid={`view-scoop-button-${scam.rank}`}
              className={`mt-3.5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 hover:gap-3 active:scale-95 ${
                scam.type === "case"
                  ? "bg-[#141915] text-white hover:bg-[#15803D]"
                  : "border border-[#2D372E] bg-white text-[#141915] hover:bg-[#141915] hover:text-white"
              }`}
            >
              View 30-second scoop
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </Link>
          ) : (
            <span
              data-testid={`coming-soon-${scam.rank}`}
              className="mt-3.5 inline-flex items-center gap-2 rounded-full border border-dashed border-[#E5DFD7] bg-[#F4EFEA] px-4 py-2 text-xs font-semibold text-[#7B887D] cursor-default select-none"
            >
              <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
              Report coming soon
            </span>
          )}
        </div>
      </div>
    </motion.article>
  );
};
